import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PopulatedEntitySuggestion } from '@bliks-io/contracts';
import {
  Badge,
  Button,
  UserCard,
  createColumnHeader,
  createDateColumn,
  getUserFullName,
  toUserCardProps,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { SuggestionSourceLabel } from '@/pages/my-tasks/components/SuggestionSourceLabel';

interface UsePendingReviewsColumnsParams {
  onReviewClick: (suggestionId: string) => void;
  onPreviewRecording: (recordingId: string) => void;
  onPreviewProcess: (
    source: Pick<PopulatedEntitySuggestion, 'sourceProcessId' | 'sourceRecordingId'>
  ) => void;
}

export function usePendingReviewsColumns({
  onReviewClick,
  onPreviewRecording,
  onPreviewProcess
}: UsePendingReviewsColumnsParams) {
  const { t } = useTranslation();

  return useMemo<DataTableColumnDef<PopulatedEntitySuggestion>[]>(
    () => [
      {
        accessorKey: 'kind',
        meta: {
          label: t('myTasks.kind'),
          filterType: 'select',
          filterOptions: [
            { label: t('entitySuggestion.kind.system'), value: 'system' },
            { label: t('entitySuggestion.kind.orgFunction'), value: 'orgFunction' }
          ]
        },
        header: createColumnHeader(t('myTasks.kind')),
        cell: ({ row }) => {
          const kind = row.original.kind;
          return (
            <Badge colorScheme={kind === 'system' ? 'indigo' : 'warning'}>
              {t(`entitySuggestion.kind.${kind}`)}
            </Badge>
          );
        },
        enableColumnFilter: true
      },
      {
        id: 'name',
        accessorFn: (row) => row.payload.name,
        meta: { label: t('myTasks.name'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.name')),
        cell: ({ row }) => <span className="font-medium">{row.original.payload.name}</span>,
        enableColumnFilter: true
      },
      {
        id: 'submittedBy',
        accessorFn: (row) => getUserFullName(row.submittedBy.firstName, row.submittedBy.lastName),
        meta: { label: t('myTasks.submittedBy'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.submittedBy')),
        cell: ({ row }) => (
          <UserCard
            user={toUserCardProps(row.original.submittedBy)}
            variant="with-role"
            itemVariant="outline"
            size="sm"
          />
        ),
        enableColumnFilter: true
      },
      {
        id: 'source',
        accessorFn: (row) => row.sourceRecordingId,
        meta: { label: t('myTasks.source') },
        header: createColumnHeader(t('myTasks.source')),
        cell: ({ row }) => {
          // Start-event suggestions carry sourceProcessId instead; the step row
          // (and its recording preview) only applies when a recording is set.
          const sourceRecordingId = row.original.sourceRecordingId;
          return (
            <div className="flex items-center gap-3 border border-border-11 rounded-lg p-2 max-w-md">
              <SuggestionSourceLabel
                suggestion={row.original}
                className="flex-1 text-sm font-normal text-black"
                onProcessClick={() => onPreviewProcess(row.original)}
                onStepClick={
                  sourceRecordingId ? () => onPreviewRecording(sourceRecordingId) : undefined
                }
              />
            </div>
          );
        },
        enableSorting: false
      },
      createDateColumn<PopulatedEntitySuggestion>('submittedAt', t('myTasks.submittedAt'), {
        format: 'datetime'
      }),
      {
        id: 'actions',
        meta: { sticky: 'right' },
        header: () => <div className="sr-only" />,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button size="small" variant="primary" onClick={() => onReviewClick(row.original._id)}>
              {t('myTasks.review')}
            </Button>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, onReviewClick, onPreviewRecording, onPreviewProcess]
  );
}
